import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchLanguages,
  fetchTranslations,
  updateTranslation,
  exportTranslations,
  importTranslations,
  clearNewEntries,
} from '../api/translations';
import type { TranslationQueryParams, UpdateTranslationRequest, ImportResult } from '../types';

export function useLanguages() {
  return useQuery({
    queryKey: ['languages'],
    queryFn: fetchLanguages,
    staleTime: 1000 * 60 * 60,
  });
}

export function useTranslations(params: TranslationQueryParams) {
  return useQuery({
    queryKey: ['translations', params],
    queryFn: () => fetchTranslations(params),
    enabled: params.language_id > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useUpdateTranslation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      translationId,
      languageId,
      data,
    }: {
      translationId: string;
      languageId: number;
      data: UpdateTranslationRequest;
    }) => updateTranslation(translationId, languageId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['translations', { language_id: variables.languageId }] 
      });
    },
  });
}

export function useExportTranslations() {
  return useMutation({
    mutationFn: (languageId: number) => exportTranslations(languageId),
  });
}

export function useImportTranslations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ languageId, file }: { languageId: number; file: File }): Promise<ImportResult> =>
      importTranslations(languageId, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['translations', { language_id: variables.languageId }]
      });
    },
  });
}

/** Marks selected entries as reviewed (clears new_entry flag) and refetches the list. */
export function useClearNewEntries() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ languageId, ids }: { languageId: number; ids: string[] }) =>
      clearNewEntries(languageId, ids),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['translations', { language_id: variables.languageId }] });
    },
  });
}
